import React, { useCallback, useEffect, useMemo } from 'react';
import type { Alignment, Font } from 'api';
import { FormatXlsxDate } from 'components/SpreadsheetNotes/utilities/FormatXLSXDate/FormatXlsxDate';
import { FormatXlsxNumber } from 'components/SpreadsheetNotes/utilities/FormatXlsxNumber/FormatXlsxNumber';
import { parseExcelColorToCss } from 'components/SpreadsheetNotes/utilities/parseExcelColorToCss';
import type { ICell } from 'models/interfaces/spreadsheet-types/core-types';

const EXCEL_DEFAULT_FONT_SIZE_PT = 11;
const EXCEL_FONT_LINE_HEIGHT = 1.2;

const getExcelFontSizePt = (font?: Font | null) => {
  const fontSize = Number(font?.size);
  return Number.isFinite(fontSize) && fontSize > 0 ? fontSize : EXCEL_DEFAULT_FONT_SIZE_PT;
};

const buildFontStyle = (font?: Font | null): React.CSSProperties => {
  const style: React.CSSProperties = {
    fontSize: `${getExcelFontSizePt(font)}pt`,
    lineHeight: EXCEL_FONT_LINE_HEIGHT,
  };

  if (!font || typeof font !== 'object') {
    return style;
  }

  style.fontWeight = font.b ? 'bold' : undefined;
  style.fontStyle = font.i ? 'italic' : undefined;
  if (font.name) {
    style.fontFamily = font.name;
  }

  const cssColor = parseExcelColorToCss(font.color);
  if (cssColor) {
    style.color = cssColor;
  }
  return style;
};

const getHorizontalAlignmentStyle = (alignment?: Alignment | null): React.CSSProperties => {
  switch (alignment?.horizontal?.toLowerCase()) {
    case 'left':
      return { justifyContent: 'flex-start', textAlign: 'left' };
    case 'center':
    case 'centercontinuous':
      return { justifyContent: 'center', textAlign: 'center' };
    case 'right':
      return { justifyContent: 'flex-end', textAlign: 'right' };
    default:
      return {};
  }
};

const getVerticalAlignmentStyle = (alignment?: Alignment | null): React.CSSProperties => {
  switch (alignment?.vertical?.toLowerCase()) {
    case 'top':
      return { alignItems: 'flex-start' };
    case 'center':
    case 'middle':
      return { alignItems: 'center' };
    case 'bottom':
      return { alignItems: 'flex-end' };
    default:
      return {};
  }
};

interface FastValueViewerProps {
  value?: any;
  cell: ICell;
  selected?: boolean;
  onDoubleClick?: (event: React.MouseEvent) => void;
  setDoubleClicked?: (value: boolean) => void;
}

const ExcelFormattedValueViewer = ({
  cell,
  selected,
  onDoubleClick,
  setDoubleClicked,
  value: cellValue,
}: FastValueViewerProps) => {
  const keyUpHandler = useCallback(
    (event: KeyboardEvent) => {
      const key = event.key || (event as unknown as { keyCode?: string | number }).keyCode;
      if (selected && key && key.toString().toUpperCase() === 'F2') {
        setDoubleClicked?.(true);
        onDoubleClick?.(event as unknown as React.MouseEvent);
      }
    },
    [onDoubleClick, selected, setDoubleClicked]
  );

  useEffect(() => {
    document.addEventListener('keyup', keyUpHandler);
    return () => document.removeEventListener('keyup', keyUpHandler);
  }, [keyUpHandler]);

  const spanId = useMemo(() => {
    if (cell?.extraAttr) {
      return `${cell.extraAttr}-${cell.alias}-${cell.key}_viewer`;
    }
    return `${cell?.alias}-${cell?.key}_viewer`;
  }, [cell]);

  const display = useMemo(() => {
    try {
      const excelProps = cell?.excelProps;
      if (!excelProps) {
        return String(cellValue ?? cell?.value ?? '');
      }
      const { format, dataType } = excelProps;

      const raw = cellValue ?? cell?.value;
      const num = typeof raw === 'number' ? raw : Number(raw);

      if (dataType === 'd' && format) {
        return FormatXlsxDate({ value: raw, format });
      }
      if (format && Number.isFinite(num) && raw !== '' && raw != null) {
        return FormatXlsxNumber({ value: num, format });
      }

      // Fallback to plain string if no excel format or non-numeric
      return raw != null ? String(raw) : '';
    } catch (_e) {
      return String(cellValue ?? cell?.value ?? '');
    }
  }, [cell, cellValue]);

  const textStyle = useMemo<React.CSSProperties>(() => {
    const font = cell?.excelProps?.font;
    const alignment = cell?.excelProps?.alignment;
    return {
      ...buildFontStyle(font),
      ...(alignment && typeof alignment.wrap_text === 'boolean'
        ? { whiteSpace: alignment.wrap_text ? ('normal' as const) : ('nowrap' as const) }
        : {}),
    };
  }, [cell]);

  const cellStyle = useMemo<React.CSSProperties>(() => {
    const alignment = cell.excelProps?.alignment;
    const horizontalAlignmentStyle = getHorizontalAlignmentStyle(alignment);
    const verticalAlignmentStyle = getVerticalAlignmentStyle(alignment);
    const isXlsxPreviewCell = cell.className?.includes('xlsx-wrap-text');

    const style: React.CSSProperties = {
      display: 'flex',
      ...(isXlsxPreviewCell && !verticalAlignmentStyle.alignItems ? { alignItems: 'flex-end' } : {}),
      ...horizontalAlignmentStyle,
      ...verticalAlignmentStyle,
    };

    if (cell.excelProps?.dataType === 's' && !alignment?.horizontal) {
      style.justifyContent = 'flex-start';
      style.textAlign = 'left';
    }

    return style;
  }, [cell]);

  return (
    <span className="value-viewer" style={cellStyle}>
      <span
        id={spanId}
        className={`text-cell ${cell?.className ?? ''}`}
        onDoubleClick={onDoubleClick}
        style={textStyle}>
        {display}
      </span>
    </span>
  );
};

export default ExcelFormattedValueViewer;
